import { type ReactNode } from 'react';
import { Button } from '../primitives/Button';
import { Modal } from '../primitives/Modal';
import { CloudIcon } from '../../icons/ui-icons';

export interface LoginPromptProps {
  open: boolean;
  onLinkGoogle: () => void;
  /** Dismisses for this session — never a hard gate, guests keep playing. */
  onDismiss: () => void;
  /** Optional brand mark shown above the eyebrow — the design system stays logo-agnostic, callers supply their own. */
  logo?: ReactNode;
  className?: string;
}

/**
 * The prompt to link a Google account, shown once per fresh session while the
 * player is still anonymous.
 *
 * A small badge in the header used to be the only place this lived — easy to
 * miss entirely, especially on a narrower window where it was hidden outright.
 * This is the fix: unmissable on first load, but still dismissible. The
 * anonymous session already works and already saves; this is a strict upgrade
 * a player can decline and be reminded of next time, never a gate that blocks
 * play — matching how `linkIdentity` itself works (it upgrades the existing
 * session in place, nothing is lost by playing as a guest first).
 */
export function LoginPrompt({ open, onLinkGoogle, onDismiss, logo, className }: LoginPromptProps) {
  return (
    <Modal
      open={open}
      onClose={onDismiss}
      size="sm"
      eyebrow="Fortschritt sichern"
      title="Mit Google anmelden?"
      className={className}
      footer={
        <>
          <Button variant="ghost" onClick={onDismiss}>
            Als Gast weiterspielen
          </Button>
          <Button variant="primary" onClick={onLinkGoogle}>
            Mit Google anmelden
          </Button>
        </>
      }
    >
      {logo ? <div className="mb-3 flex justify-center">{logo}</div> : null}
      <div className="flex items-start gap-3">
        <CloudIcon size={22} className="mt-0.5 shrink-0 text-content-muted" />
        <p className="text-xs leading-relaxed text-content-secondary">
          Dein Spielstand liegt bisher nur auf diesem Gerät. Verknüpfe ein Google-Konto, um
          denselben Fortschritt auf jedem Browser und Gerät weiterzuspielen — dein aktueller
          Stand bleibt dabei erhalten, es beginnt nichts neu.
        </p>
      </div>
    </Modal>
  );
}
